/**
 * ForgotPasswordDialog — herbruikbare dialog voor "wachtwoord vergeten".
 * Toont een email-input, roept endpoint aan (employee of klant), toont
 * duidelijke feedback (mail verstuurd of email onbekend).
 */
import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordDialog({ open, onClose, mode = 'employee' }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const endpoint = mode === 'klant'
    ? `${API_URL}/api/klant-portal/auth/forgot-password`
    : `${API_URL}/api/auth/forgot-password`;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.post(endpoint, { email });
      toast.success(r.data?.message || `Reset-link verzonden naar ${email}`, { duration: 6000 });
      onClose();
      setEmail('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Er ging iets mis. Probeer het opnieuw.', {
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="forgot-password-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4" /> Wachtwoord vergeten
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded"
            data-testid="forgot-password-close"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <p className="text-sm text-slate-600">
            Vul het emailadres van je {mode === 'klant' ? 'klant-portal' : 'WASZ.nl'} account in.
            We sturen je een reset-link (24 uur geldig).
          </p>
          <div>
            <Label>Emailadres</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="jij@voorbeeld.nl"
              autoFocus
              data-testid="forgot-password-email"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="forgot-password-cancel"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={loading || !email}
              className="flex-1"
              data-testid="forgot-password-submit"
            >
              {loading ? 'Bezig...' : 'Verstuur link'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
